<template>
  <div class="documentation-list">
    <DocumentationListHeader />

    <DocumentationListLoader v-if="isLoading" />

    <div v-else-if="hasDocumentation" class="documentation-list__content">
      <!-- Show multiple documentation groups if they exist -->
      <DocumentationListContent
        v-for="(documentation, index) in documentationEntries"
        :key="`documentation-${index}`"
        :documentation="documentation"
        :supplier-id="supplierId"
        class="documentation-list__item"
      />
    </div>

    <DocumentationListEmptyState v-else />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { formatDateInNorwegian } from "@/utilities/dateUtils";
import { useDocumentationCache } from "@/composables/useDocumentationCache";
import type { ServiceOffer } from "@/custom-types/serviceOffers";
import type { DocumentationRecord } from "@/types/service-records";

import DocumentationListHeader from "./components/DocumentationListHeader.vue";
import DocumentationListLoader from "./components/DocumentationListLoader.vue";
import DocumentationListContent from "./components/DocumentationListContent.vue";
import DocumentationListEmptyState from "./components/DocumentationListEmptyState.vue";

interface Props {
  serviceOfferId: number;
  tenderId: number;
  customerOrgNumber: string;
  supplierId: number;
  acceptedServiceOffer?: ServiceOffer | null;
  localDocumentation?: DocumentationRecord | null;
}

interface DocumentationData {
  description: string | null;
  attachmentGroups?: ServiceOffer["supplierRequestAttachmentGroups"];
  legacyAttachments?: ServiceOffer["supplierRequestAttachments"];
  date: string;
}

const props = defineProps<Props>();

// Composables
const { getCachedDocumentation } = useDocumentationCache();

// Computed properties following the Extract Logic pattern
const isLoading = computed(() => false);

const hasDocumentation = computed(() => {
  return documentationEntries.value.length > 0;
});

const documentationEntries = computed((): DocumentationData[] => {
  const entries: DocumentationData[] = [];

  // Priority 1: Local documentation (from recent form submission)
  if (props.localDocumentation) {
    entries.push({
      description: props.localDocumentation.description || null,
      legacyAttachments: props.localDocumentation.attachments || [],
      date: formatDateInNorwegian(
        props.localDocumentation.createdAt || new Date().toISOString(),
        "DD.MM.YYYY",
      ),
    });
    return entries;
  }

  // Priority 2: Cached documentation (from localStorage)
  const cached = getCachedDocumentation(props.serviceOfferId);
  if (cached) {
    entries.push({
      description: cached.description || null,
      legacyAttachments: cached.attachments || [],
      date: formatDateInNorwegian(cached.createdAt, "DD.MM.YYYY"),
    });
    return entries;
  }

  // Priority 3: AcceptedServiceOffer documentation (new structure preferred)
  const serviceOffer = props.acceptedServiceOffer;
  if (serviceOffer?.supplierRequestAttachmentGroups?.length) {
    // Create separate entries for each attachment group
    serviceOffer.supplierRequestAttachmentGroups.forEach((group) => {
      entries.push({
        description: group.description || null,
        attachmentGroups: [group], // Wrap single group in array for consistency
        date: formatDateInNorwegian(
          serviceOffer.acceptanceDate || new Date().toISOString(),
          "DD.MM.YYYY",
        ),
      });
    });
    return entries;
  }

  // Fallback to legacy structure
  if (
    serviceOffer?.supplierRequestAttachments?.length ||
    serviceOffer?.supplierRequestAttachmentsDescription
  ) {
    entries.push({
      description: serviceOffer.supplierRequestAttachmentsDescription || null,
      legacyAttachments: serviceOffer.supplierRequestAttachments || [],
      date: formatDateInNorwegian(
        serviceOffer.acceptanceDate || new Date().toISOString(),
        "DD.MM.YYYY",
      ),
    });
  }

  return entries;
});
</script>

<style lang="scss" scoped>
.documentation-list {
  width: 100%;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__item {
    &:not(:last-child) {
      border-bottom: 1px solid $color-border-light;
      padding-bottom: 1.5rem;
    }
  }
}
</style>
